import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import LinearProgress from '@mui/material/LinearProgress';
// components
import Iconify from '../iconify';

// ----------------------------------------------------------------------

export function RHFPdfUpload({ name, label, helperText, maxSize = 5242880, ...other }) {
    const { control, setValue } = useFormContext();
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (file) {
                // Simulate upload progress
                setUploadProgress(0);
                const interval = setInterval(() => {
                    setUploadProgress((prev) => {
                        if (prev >= 100) {
                            clearInterval(interval);
                            return 100;
                        }
                        return prev + 20;
                    });
                }, 100);

                const newFile = Object.assign(file, {
                    preview: URL.createObjectURL(file),
                });
                setValue(name, newFile, { shouldValidate: true });
            }
        },
        [setValue, name]
    );

    const handleRemove = useCallback(() => {
        setValue(name, null);
        setUploadProgress(0);
    }, [setValue, name]);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop: handleDrop,
        accept: {
            'application/pdf': ['.pdf'],
        },
        maxSize,
        multiple: false,
    });

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const hasFile = !!field.value;
                const isError = isDragReject || !!error;

                return (
                    <Box>
                        {!hasFile ? (
                            <Box
                                {...getRootProps()}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                                    border: (theme) => `2px dashed ${alpha(theme.palette.grey[500], 0.24)}`,
                                    transition: (theme) => theme.transitions.create(['all']),
                                    '&:hover': {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                        borderColor: 'primary.main',
                                    },
                                    ...(isDragActive && {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                        borderColor: 'primary.main',
                                    }),
                                    ...(isError && {
                                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.04),
                                        borderColor: 'error.main',
                                    }),
                                }}
                            >
                                <input {...getInputProps()} />

                                <Stack spacing={2} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                                            color: 'error.main',
                                        }}
                                    >
                                        <Iconify icon="vscode-icons:file-type-pdf2" width={32} />
                                    </Box>

                                    <Stack spacing={0.5} alignItems="center">
                                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                                            {isDragActive ? 'Drop PDF here' : 'Upload PDF Document'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Drop PDF here or click to{' '}
                                            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                                                browse
                                            </Box>
                                        </Typography>
                                    </Stack>

                                    <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center' }}>
                                        {helperText || 'Allowed: PDF only • Max size: 5MB'}
                                    </Typography>
                                </Stack>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.04),
                                    border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Iconify icon="vscode-icons:file-type-pdf2" width={28} />
                                    </Box>

                                    <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" noWrap>
                                            {field.value?.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {field.value?.size ? `${(field.value.size / 1024 / 1024).toFixed(2)} MB` : ''}
                                        </Typography>
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={uploadProgress}
                                                sx={{
                                                    height: 4,
                                                    borderRadius: 1,
                                                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: 'error.main',
                                                    },
                                                }}
                                            />
                                        )}
                                    </Stack>

                                    <IconButton
                                        size="small"
                                        onClick={handleRemove}
                                        sx={{
                                            color: 'error.main',
                                            '&:hover': {
                                                bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                                            },
                                        }}
                                    >
                                        <Iconify icon="mingcute:close-line" />
                                    </IconButton>
                                </Stack>
                            </Box>
                        )}

                        {!!error && (
                            <FormHelperText error sx={{ px: 2, mt: 1 }}>
                                {error.message}
                            </FormHelperText>
                        )}
                    </Box>
                );
            }}
        />
    );
}

RHFPdfUpload.propTypes = {
    helperText: PropTypes.string,
    label: PropTypes.string,
    maxSize: PropTypes.number,
    name: PropTypes.string.isRequired,
};
